import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { 
  FilmStrip, 
  Plus, 
  Trash, 
  Play, 
  Pause, 
  Users, 
  FileText, 
  Image as ImageIcon,
  SpeakerHigh,
  Download,
  Check,
  Spinner,
  CaretRight,
  X,
  MagicWand,
  Folder,
  VideoCamera,
  Export
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get image URL - handles both base64 and file references
const getImageUrl = (scene) => {
  if (!scene?.image_generated) return null;
  if (scene.image_base64?.startsWith('FILE:')) {
    // Load from file endpoint
    return `${API}/assets/image/${scene.id}`;
  }
  if (scene.image_base64) {
    return `data:image/png;base64,${scene.image_base64}`;
  }
  // Fallback to file endpoint
  return `${API}/assets/image/${scene.id}`;
};

// Ken Burns Animation Component
const KenBurnsImage = ({ src, isPlaying, duration = 10 }) => {
  const [animationState, setAnimationState] = useState(0);
  
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setAnimationState(prev => (prev + 1) % 4);
    }, duration * 1000 / 4);
    return () => clearInterval(interval);
  }, [isPlaying, duration]);
  
  const transforms = [
    { scale: 1, x: 0, y: 0 },
    { scale: 1.1, x: -20, y: -10 },
    { scale: 1.15, x: 10, y: -20 },
    { scale: 1.05, x: -10, y: 10 }
  ];
  
  const current = transforms[animationState];
  
  return (
    <motion.img
      src={src}
      alt="Scene"
      className="w-full h-full object-cover"
      animate={{
        scale: current.scale,
        x: current.x,
        y: current.y
      }}
      transition={{ duration: duration / 4, ease: "easeInOut" }}
    />
  );
};

// Status Polling Hook
const useStatusPolling = (statusId, onComplete) => {
  useEffect(() => {
    if (!statusId) return;
    
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/status/${statusId}`);
        if (res.data.status === "completed") {
          onComplete(res.data);
        } else if (res.data.status === "error") {
          console.error("Error:", res.data.message);
          onComplete(null, res.data.message);
        } else {
          setTimeout(poll, 2000);
        }
      } catch (e) {
        console.error(e);
      }
    };
    
    poll();
  }, [statusId, onComplete]);
};

function App() {
  // State
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [characters, setCharacters] = useState({});
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState("fable");
  
  // UI State
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewChapter, setShowNewChapter] = useState(false);
  const [newProjectTitle, setNewProjectTitle] = useState("");
  const [newChapterTitle, setNewChapterTitle] = useState("");
  const [newChapterContent, setNewChapterContent] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeStatusId, setAnalyzeStatusId] = useState(null);
  const [generatingImageId, setGeneratingImageId] = useState(null);
  const [generatingAudioId, setGeneratingAudioId] = useState(null);
  const [imageStatusId, setImageStatusId] = useState(null);
  const [audioStatusId, setAudioStatusId] = useState(null);
  
  // Batch Generation State
  const [isBatchGenerating, setIsBatchGenerating] = useState(false);
  const [batchStatusId, setBatchStatusId] = useState(null);
  const [batchProgress, setBatchProgress] = useState({ progress: 0, message: "", currentScene: 0, total: 0 });
  
  // Video Export State
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedChaptersForExport, setSelectedChaptersForExport] = useState([]);
  const [exportTitle, setExportTitle] = useState("The Unmaking - Teaser");
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ progress: 0, message: "" });
  const [exportedVideos, setExportedVideos] = useState([]);
  
  // Preview State
  const [selectedScene, setSelectedScene] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);

  // Load initial data
  useEffect(() => {
    loadProjects();
    loadVoices();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch (e) {
      console.error("Failed to load projects:", e);
    }
  };

  const loadVoices = async () => {
    try {
      const res = await axios.get(`${API}/voices`);
      setVoices(res.data.voices);
    } catch (e) {
      console.error("Failed to load voices:", e);
    }
  };

  const loadExportedVideos = async () => {
    try {
      const res = await axios.get(`${API}/export/videos`);
      setExportedVideos(res.data.videos);
    } catch (e) {
      console.error("Failed to load videos:", e);
    }
  };

  const exportVideo = async () => {
    if (selectedChaptersForExport.length === 0) return;
    setIsExporting(true);
    setExportProgress({ progress: 0, message: "Starting export..." });
    
    try {
      const res = await axios.post(`${API}/export/video`, {
        chapter_ids: selectedChaptersForExport,
        title: exportTitle,
        include_chapter_titles: true
      });
      
      const statusId = res.data.status_id;
      
      // Poll for progress
      const poll = async () => {
        const statusRes = await axios.get(`${API}/status/${statusId}`);
        const status = statusRes.data;
        
        setExportProgress({
          progress: status.progress,
          message: status.message
        });
        
        if (status.status === "completed") {
          setIsExporting(false);
          loadExportedVideos();
          // Keep modal open to show download link
          setExportProgress({
            progress: 100,
            message: status.message,
            downloadUrl: status.result?.download_url
          });
        } else if (status.status === "error") {
          setIsExporting(false);
          setExportProgress({ progress: 0, message: `Error: ${status.message}` });
        } else {
          setTimeout(poll, 2000);
        }
      };
      
      poll();
    } catch (e) {
      console.error("Failed to export video:", e);
      setIsExporting(false);
      setExportProgress({ progress: 0, message: "Export failed" });
    }
  };

  const toggleChapterForExport = (chapterId) => {
    setSelectedChaptersForExport(prev => 
      prev.includes(chapterId) 
        ? prev.filter(id => id !== chapterId)
        : [...prev, chapterId]
    );
  };

  const selectAllChaptersForExport = () => {
    const analyzedChapters = chapters.filter(ch => ch.status === "analyzed" && ch.scenes?.length > 0);
    setSelectedChaptersForExport(analyzedChapters.map(ch => ch.id));
  };

  const loadProject = async (projectId) => {
    try {
      const [projectRes, chaptersRes, charsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/chapters`),
        axios.get(`${API}/projects/${projectId}/characters`)
      ]);
      setCurrentProject(projectRes.data);
      setChapters(chaptersRes.data);
      setCharacters(charsRes.data.characters);
      setCurrentChapter(null);
      setSelectedScene(null);
    } catch (e) {
      console.error("Failed to load project:", e);
    }
  };

  const createProject = async () => {
    if (!newProjectTitle.trim()) return;
    try {
      const res = await axios.post(`${API}/projects`, { title: newProjectTitle });
      setProjects([...projects, res.data]);
      setCurrentProject(res.data);
      setChapters([]);
      setCharacters({});
      setNewProjectTitle("");
      setShowNewProject(false);
    } catch (e) {
      console.error("Failed to create project:", e);
    }
  };

  const deleteProject = async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      setProjects(projects.filter(p => p.id !== projectId));
      if (currentProject?.id === projectId) {
        setCurrentProject(null);
        setChapters([]);
        setCharacters({});
      }
    } catch (e) {
      console.error("Failed to delete project:", e);
    }
  };

  const createChapter = async () => {
    if (!newChapterTitle.trim() || !newChapterContent.trim() || !currentProject) return;
    try {
      const res = await axios.post(`${API}/chapters`, {
        project_id: currentProject.id,
        chapter_number: chapters.length + 1,
        title: newChapterTitle,
        content: newChapterContent
      });
      setChapters([...chapters, res.data]);
      setNewChapterTitle("");
      setNewChapterContent("");
      setShowNewChapter(false);
    } catch (e) {
      console.error("Failed to create chapter:", e);
    }
  };

  const analyzeChapter = async (chapterId) => {
    setIsAnalyzing(true);
    try {
      const res = await axios.post(`${API}/chapters/${chapterId}/analyze`);
      setAnalyzeStatusId(res.data.status_id);
    } catch (e) {
      console.error("Failed to analyze chapter:", e);
      setIsAnalyzing(false);
    }
  };

  // Status polling callbacks
  const handleAnalyzeComplete = useCallback(async (data, error) => {
    setIsAnalyzing(false);
    setAnalyzeStatusId(null);
    if (data && currentChapter) {
      // Reload chapter to get updated scenes
      const res = await axios.get(`${API}/chapters/${currentChapter.id}`);
      setCurrentChapter(res.data);
      // Reload characters
      if (currentProject) {
        const charsRes = await axios.get(`${API}/projects/${currentProject.id}/characters`);
        setCharacters(charsRes.data.characters);
      }
    }
  }, [currentChapter, currentProject]);

  const handleImageComplete = useCallback(async (data, error) => {
    setGeneratingImageId(null);
    setImageStatusId(null);
    if (data && currentChapter) {
      const res = await axios.get(`${API}/chapters/${currentChapter.id}`);
      setCurrentChapter(res.data);
      // Update selected scene if it matches
      if (selectedScene) {
        const updated = res.data.scenes.find(s => s.id === selectedScene.id);
        if (updated) setSelectedScene(updated);
      }
    }
  }, [currentChapter, selectedScene]);

  const handleAudioComplete = useCallback(async (data, error) => {
    setGeneratingAudioId(null);
    setAudioStatusId(null);
    if (data && currentChapter) {
      const res = await axios.get(`${API}/chapters/${currentChapter.id}`);
      setCurrentChapter(res.data);
      if (selectedScene) {
        const updated = res.data.scenes.find(s => s.id === selectedScene.id);
        if (updated) setSelectedScene(updated);
      }
    }
  }, [currentChapter, selectedScene]);

  // Batch generation complete handler
  const handleBatchComplete = useCallback(async (data, error) => {
    setIsBatchGenerating(false);
    setBatchStatusId(null);
    setBatchProgress({ progress: 0, message: "", currentScene: 0, total: 0 });
    if (data && currentChapter) {
      const res = await axios.get(`${API}/chapters/${currentChapter.id}`);
      setCurrentChapter(res.data);
    }
  }, [currentChapter]);

  // Custom polling for batch with progress updates
  useEffect(() => {
    if (!batchStatusId) return;
    
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/status/${batchStatusId}`);
        setBatchProgress({
          progress: res.data.progress,
          message: res.data.message,
          currentScene: res.data.result?.current_scene || 0,
          total: res.data.result?.total || 0
        });
        
        if (res.data.status === "completed") {
          handleBatchComplete(res.data);
        } else if (res.data.status === "error") {
          console.error("Batch error:", res.data.message);
          handleBatchComplete(null, res.data.message);
        } else {
          setTimeout(poll, 2000);
        }
      } catch (e) {
        console.error(e);
      }
    };
    
    poll();
  }, [batchStatusId, handleBatchComplete]);

  useStatusPolling(analyzeStatusId, handleAnalyzeComplete);
  useStatusPolling(imageStatusId, handleImageComplete);
  useStatusPolling(audioStatusId, handleAudioComplete);

  // Generate All function
  const generateAllScenes = async () => {
    if (!currentChapter) return;
    setIsBatchGenerating(true);
    setBatchProgress({ progress: 0, message: "Starting batch generation...", currentScene: 0, total: currentChapter.scenes?.length || 0 });
    
    try {
      const res = await axios.post(`${API}/chapters/${currentChapter.id}/generate-all`, {
        chapter_id: currentChapter.id,
        voice: selectedVoice,
        generate_images: true,
        generate_narration: true
      });
      setBatchStatusId(res.data.status_id);
    } catch (e) {
      console.error("Failed to start batch generation:", e);
      setIsBatchGenerating(false);
    }
  };

  const generateSceneImage = async (scene) => {
    if (!currentChapter) return;
    setGeneratingImageId(scene.id);
    try {
      const res = await axios.post(`${API}/scenes/generate-image`, {
        scene_id: scene.id,
        chapter_id: currentChapter.id,
        prompt: scene.image_prompt
      });
      setImageStatusId(res.data.status_id);
    } catch (e) {
      console.error("Failed to generate image:", e);
      setGeneratingImageId(null);
    }
  };

  const generateSceneNarration = async (scene) => {
    if (!currentChapter) return;
    setGeneratingAudioId(scene.id);
    try {
      const res = await axios.post(`${API}/scenes/generate-narration`, {
        scene_id: scene.id,
        chapter_id: currentChapter.id,
        text: scene.narration_text,
        voice: selectedVoice
      });
      setAudioStatusId(res.data.status_id);
    } catch (e) {
      console.error("Failed to generate narration:", e);
      setGeneratingAudioId(null);
    }
  };

  const playScene = (scene) => {
    setSelectedScene(scene);
    if (scene.audio_base64) {
      if (currentAudio) {
        currentAudio.pause();
      }
      const audio = new Audio(`data:audio/mp3;base64,${scene.audio_base64}`);
      audio.onended = () => setIsPlaying(false);
      setCurrentAudio(audio);
      audio.play();
      setIsPlaying(true);
    } else {
      setIsPlaying(true);
    }
  };

  const togglePlayPause = () => {
    if (currentAudio) {
      if (isPlaying) {
        currentAudio.pause();
      } else {
        currentAudio.play();
      }
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div className="h-screen bg-[#09090B] text-[#FAFAFA] flex flex-col overflow-hidden">
      {/* Header */}
      <header className="h-14 border-b border-white/10 flex items-center px-4 shrink-0 justify-between">
        <div className="flex items-center">
          <FilmStrip size={28} weight="fill" className="text-amber-500 mr-3" />
          <h1 className="font-serif text-xl tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Story Animator
          </h1>
          {currentProject && (
            <span className="ml-4 text-sm text-zinc-500 font-mono">
              / {currentProject.title}
            </span>
          )}
        </div>
        {currentProject && chapters.length > 0 && (
          <button
            onClick={() => {
              setShowExportModal(true);
              loadExportedVideos();
              selectAllChaptersForExport();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-sm hover:from-purple-500 hover:to-pink-500 transition-all"
            data-testid="export-video-btn"
          >
            <VideoCamera size={18} weight="fill" />
            Export Video
          </button>
        )}
      </header>

      {/* Main Content */}
      <div className="flex-1 grid grid-cols-12 gap-0 min-h-0">
        {/* Left Panel - Projects & Chapters */}
        <div className="col-span-3 border-r border-white/10 flex flex-col min-h-0" data-testid="left-panel">
          {/* Projects Section */}
          <div className="border-b border-white/10 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Projects</span>
              <button 
                onClick={() => setShowNewProject(true)}
                className="p-1 hover:bg-white/10 rounded transition-colors"
                data-testid="new-project-btn"
              >
                <Plus size={16} />
              </button>
            </div>
            
            <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="projects-list">
              {projects.map(project => (
                <div 
                  key={project.id}
                  className={`flex items-center justify-between p-2 cursor-pointer transition-colors rounded-sm ${
                    currentProject?.id === project.id ? 'bg-amber-500/20 border border-amber-500/30' : 'hover:bg-white/5'
                  }`}
                  onClick={() => loadProject(project.id)}
                  data-testid={`project-${project.id}`}
                >
                  <div className="flex items-center gap-2">
                    <Folder size={16} className="text-amber-500" />
                    <span className="text-sm truncate">{project.title}</span>
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); deleteProject(project.id); }}
                    className="p-1 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-project-${project.id}`}
                  >
                    <Trash size={14} className="text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Chapters Section */}
          {currentProject && (
            <div className="flex-1 p-3 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Chapters</span>
                <button 
                  onClick={() => setShowNewChapter(true)}
                  className="p-1 hover:bg-white/10 rounded transition-colors"
                  data-testid="new-chapter-btn"
                >
                  <Plus size={16} />
                </button>
              </div>
              
              <div className="space-y-1 overflow-y-auto flex-1">
                {chapters.map(chapter => (
                  <div 
                    key={chapter.id}
                    className={`p-2 cursor-pointer transition-colors rounded-sm ${
                      currentChapter?.id === chapter.id ? 'bg-white/10 border border-white/20' : 'hover:bg-white/5'
                    }`}
                    onClick={() => setCurrentChapter(chapter)}
                    data-testid={`chapter-${chapter.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-zinc-400" />
                      <span className="text-sm">Ch {chapter.chapter_number}: {chapter.title}</span>
                    </div>
                    <div className="flex gap-2 mt-1 ml-6">
                      {chapter.status === "analyzed" && (
                        <span className="text-xs text-green-400 flex items-center gap-1">
                          <Check size={10} /> Analyzed
                        </span>
                      )}
                      {chapter.scenes?.length > 0 && (
                        <span className="text-xs text-zinc-500">{chapter.scenes.length} scenes</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Characters Section */}
          {currentProject && Object.keys(characters).length > 0 && (
            <div className="border-t border-white/10 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Users size={16} className="text-amber-500" />
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Characters</span>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {Object.entries(characters).map(([name, info]) => (
                  <div key={name} className="p-2 bg-white/5 rounded-sm">
                    <span className="text-sm font-medium text-amber-400">{name}</span>
                    <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{info.physical_description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Center Panel - Preview */}
        <div className="col-span-6 flex flex-col min-h-0" data-testid="center-panel">
          {/* Video Preview Area */}
          <div className="flex-1 flex items-center justify-center bg-black/50 relative overflow-hidden">
            {selectedScene?.image_generated ? (
              <div className="w-full h-full relative overflow-hidden">
                <KenBurnsImage 
                  src={getImageUrl(selectedScene)}
                  isPlaying={isPlaying}
                />
                {/* Play/Pause Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <button 
                    onClick={togglePlayPause}
                    className="w-16 h-16 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center hover:bg-black/80 transition-colors"
                    data-testid="play-pause-btn"
                  >
                    {isPlaying ? <Pause size={32} weight="fill" /> : <Play size={32} weight="fill" />}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center text-zinc-500">
                <FilmStrip size={64} className="mx-auto mb-4 opacity-30" />
                <p className="text-sm">Select a scene to preview</p>
              </div>
            )}
          </div>

          {/* Timeline */}
          {currentChapter?.scenes?.length > 0 && (
            <div className="h-32 border-t border-white/10 p-3 shrink-0" data-testid="timeline">
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Timeline</span>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {currentChapter.scenes.map((scene, idx) => (
                  <div 
                    key={scene.id}
                    className={`shrink-0 w-24 cursor-pointer transition-all ${
                      selectedScene?.id === scene.id ? 'ring-2 ring-amber-500' : ''
                    }`}
                    onClick={() => setSelectedScene(scene)}
                    data-testid={`timeline-scene-${scene.id}`}
                  >
                    <div className="h-14 bg-zinc-800 rounded-sm overflow-hidden relative">
                      {scene.image_generated ? (
                        <img 
                          src={getImageUrl(scene)} 
                          alt={`Scene ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon size={20} className="text-zinc-600" />
                        </div>
                      )}
                      <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-xs px-1 text-center">
                        {idx + 1}
                      </span>
                    </div>
                    {scene.audio_generated && (
                      <div className="h-2 bg-blue-500/50 mt-1 rounded-sm"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Controls */}
        <div className="col-span-3 border-l border-white/10 flex flex-col min-h-0" data-testid="right-panel">
          {currentChapter ? (
            <>
              {/* Chapter Info */}
              <div className="p-3 border-b border-white/10">
                <h3 className="font-serif text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Chapter {currentChapter.chapter_number}
                </h3>
                <p className="text-amber-400 text-sm">{currentChapter.title}</p>
                
                {currentChapter.status !== "analyzed" && (
                  <button
                    onClick={() => analyzeChapter(currentChapter.id)}
                    disabled={isAnalyzing}
                    className="mt-3 w-full py-2 px-4 bg-amber-500 text-amber-950 font-medium rounded-sm hover:bg-amber-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="analyze-chapter-btn"
                  >
                    {isAnalyzing ? (
                      <>
                        <Spinner size={16} className="animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <MagicWand size={16} />
                        Extract Scenes & Characters
                      </>
                    )}
                  </button>
                )}

                {/* Generate All Button */}
                {currentChapter.status === "analyzed" && currentChapter.scenes?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {/* Voice Selection for Batch */}
                    <div>
                      <label className="text-xs text-zinc-500 block mb-1">Narrator Voice</label>
                      <select
                        value={selectedVoice}
                        onChange={(e) => setSelectedVoice(e.target.value)}
                        className="w-full bg-transparent border border-white/10 rounded-sm px-2 py-1.5 text-sm"
                        data-testid="batch-voice-select"
                        disabled={isBatchGenerating}
                      >
                        {voices.map(voice => (
                          <option key={voice.id} value={voice.id} className="bg-zinc-900">
                            {voice.name} - {voice.description}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {isBatchGenerating ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-zinc-400">Generating...</span>
                          <span className="text-amber-400">{batchProgress.currentScene}/{batchProgress.total}</span>
                        </div>
                        <div className="w-full bg-zinc-800 rounded-full h-2">
                          <div 
                            className="bg-amber-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${batchProgress.progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-zinc-500 truncate">{batchProgress.message}</p>
                      </div>
                    ) : (
                      <button
                        onClick={generateAllScenes}
                        className="w-full py-2 px-4 bg-gradient-to-r from-amber-500 to-orange-500 text-amber-950 font-medium rounded-sm hover:from-amber-400 hover:to-orange-400 transition-all flex items-center justify-center gap-2"
                        data-testid="generate-all-btn"
                      >
                        <MagicWand size={16} weight="fill" />
                        Generate All ({currentChapter.scenes.length} scenes)
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Scene Details */}
              {selectedScene && (
                <div className="flex-1 p-3 overflow-y-auto">
                  <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">
                    Scene {selectedScene.scene_number}
                  </span>
                  <p className="text-sm text-zinc-300 mt-2">{selectedScene.description}</p>
                  
                  {/* Characters in scene */}
                  {selectedScene.characters_present?.length > 0 && (
                    <div className="mt-3">
                      <span className="text-xs text-zinc-500">Characters:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedScene.characters_present.map(char => (
                          <span key={char} className="text-xs bg-white/10 px-2 py-0.5 rounded-sm">
                            {char}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mood */}
                  {selectedScene.mood && (
                    <div className="mt-3">
                      <span className="text-xs text-zinc-500">Mood:</span>
                      <span className="text-xs text-amber-400 ml-2">{selectedScene.mood}</span>
                    </div>
                  )}

                  {/* Generation Controls */}
                  <div className="mt-4 space-y-2">
                    <button
                      onClick={() => generateSceneImage(selectedScene)}
                      disabled={generatingImageId === selectedScene.id}
                      className="w-full py-2 px-3 border border-white/10 rounded-sm hover:bg-white/5 transition-colors flex items-center justify-between disabled:opacity-50"
                      data-testid="generate-image-btn"
                    >
                      <span className="flex items-center gap-2">
                        <ImageIcon size={16} />
                        {selectedScene.image_generated ? 'Regenerate Image' : 'Generate Image'}
                      </span>
                      {generatingImageId === selectedScene.id && (
                        <Spinner size={16} className="animate-spin" />
                      )}
                      {selectedScene.image_generated && generatingImageId !== selectedScene.id && (
                        <Check size={16} className="text-green-400" />
                      )}
                    </button>

                    {/* Voice Selection */}
                    <div className="mt-2">
                      <label className="text-xs text-zinc-500 block mb-1">Narrator Voice</label>
                      <select
                        value={selectedVoice}
                        onChange={(e) => setSelectedVoice(e.target.value)}
                        className="w-full bg-transparent border border-white/10 rounded-sm px-2 py-1.5 text-sm"
                        data-testid="voice-select"
                      >
                        {voices.map(voice => (
                          <option key={voice.id} value={voice.id} className="bg-zinc-900">
                            {voice.name} - {voice.description}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      onClick={() => generateSceneNarration(selectedScene)}
                      disabled={generatingAudioId === selectedScene.id}
                      className="w-full py-2 px-3 border border-white/10 rounded-sm hover:bg-white/5 transition-colors flex items-center justify-between disabled:opacity-50"
                      data-testid="generate-narration-btn"
                    >
                      <span className="flex items-center gap-2">
                        <SpeakerHigh size={16} />
                        {selectedScene.audio_generated ? 'Regenerate Narration' : 'Generate Narration'}
                      </span>
                      {generatingAudioId === selectedScene.id && (
                        <Spinner size={16} className="animate-spin" />
                      )}
                      {selectedScene.audio_generated && generatingAudioId !== selectedScene.id && (
                        <Check size={16} className="text-green-400" />
                      )}
                    </button>

                    {/* Play Scene */}
                    {selectedScene.image_generated && (
                      <button
                        onClick={() => playScene(selectedScene)}
                        className="w-full py-2 px-3 bg-amber-500 text-amber-950 font-medium rounded-sm hover:bg-amber-400 transition-colors flex items-center justify-center gap-2"
                        data-testid="play-scene-btn"
                      >
                        <Play size={16} weight="fill" />
                        Play Scene
                      </button>
                    )}

                    {/* Download Assets */}
                    {(selectedScene.image_generated || selectedScene.audio_generated) && (
                      <div className="flex gap-2 mt-3">
                        {selectedScene.image_generated && (
                          <a
                            href={`${API}/assets/image/${selectedScene.id}`}
                            download
                            className="flex-1 py-2 px-3 border border-white/10 rounded-sm hover:bg-white/5 transition-colors flex items-center justify-center gap-2 text-sm"
                            data-testid="download-image-btn"
                          >
                            <Download size={14} />
                            Image
                          </a>
                        )}
                        {selectedScene.audio_generated && (
                          <a
                            href={`${API}/assets/audio/${selectedScene.id}`}
                            download
                            className="flex-1 py-2 px-3 border border-white/10 rounded-sm hover:bg-white/5 transition-colors flex items-center justify-center gap-2 text-sm"
                            data-testid="download-audio-btn"
                          >
                            <Download size={14} />
                            Audio
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Scenes List */}
              {currentChapter.scenes?.length > 0 && !selectedScene && (
                <div className="flex-1 p-3 overflow-y-auto">
                  <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2 block">
                    Scenes ({currentChapter.scenes.length})
                  </span>
                  <div className="space-y-2">
                    {currentChapter.scenes.map((scene, idx) => (
                      <div
                        key={scene.id}
                        className="p-2 border border-white/10 rounded-sm cursor-pointer hover:bg-white/5 transition-colors"
                        onClick={() => setSelectedScene(scene)}
                        data-testid={`scene-card-${scene.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Scene {idx + 1}</span>
                          <div className="flex gap-1">
                            {scene.image_generated && <ImageIcon size={14} className="text-green-400" />}
                            {scene.audio_generated && <SpeakerHigh size={14} className="text-blue-400" />}
                          </div>
                        </div>
                        <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{scene.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-zinc-500">
              <div className="text-center">
                <FileText size={48} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">Select a chapter to view</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Project Modal */}
      <AnimatePresence>
        {showNewProject && (
          <motion.div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="bg-[#121214] border border-white/10 p-6 w-full max-w-md"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              data-testid="new-project-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  New Project
                </h2>
                <button onClick={() => setShowNewProject(false)} className="p-1 hover:bg-white/10 rounded">
                  <X size={20} />
                </button>
              </div>
              <input
                type="text"
                placeholder="Project title (e.g., My Epic Story)"
                value={newProjectTitle}
                onChange={(e) => setNewProjectTitle(e.target.value)}
                className="w-full bg-transparent border border-white/10 rounded-sm px-3 py-2 mb-4 focus:outline-none focus:border-amber-500"
                data-testid="project-title-input"
              />
              <button
                onClick={createProject}
                disabled={!newProjectTitle.trim()}
                className="w-full py-2 bg-amber-500 text-amber-950 font-medium rounded-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
                data-testid="create-project-btn"
              >
                Create Project
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* New Chapter Modal */}
      <AnimatePresence>
        {showNewChapter && (
          <motion.div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="bg-[#121214] border border-white/10 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              data-testid="new-chapter-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Add Chapter {chapters.length + 1}
                </h2>
                <button onClick={() => setShowNewChapter(false)} className="p-1 hover:bg-white/10 rounded">
                  <X size={20} />
                </button>
              </div>
              <input
                type="text"
                placeholder="Chapter title (e.g., The Beginning)"
                value={newChapterTitle}
                onChange={(e) => setNewChapterTitle(e.target.value)}
                className="w-full bg-transparent border border-white/10 rounded-sm px-3 py-2 mb-4 focus:outline-none focus:border-amber-500"
                data-testid="chapter-title-input"
              />
              <textarea
                placeholder="Paste your chapter content here..."
                value={newChapterContent}
                onChange={(e) => setNewChapterContent(e.target.value)}
                rows={15}
                className="w-full bg-transparent border border-white/10 rounded-sm px-3 py-2 mb-4 focus:outline-none focus:border-amber-500 resize-none font-mono text-sm"
                data-testid="chapter-content-input"
              />
              <p className="text-xs text-zinc-500 mb-4">
                The AI will automatically extract characters and break this into animated scenes.
              </p>
              <button
                onClick={createChapter}
                disabled={!newChapterTitle.trim() || !newChapterContent.trim()}
                className="w-full py-2 bg-amber-500 text-amber-950 font-medium rounded-sm hover:bg-amber-400 transition-colors disabled:opacity-50"
                data-testid="create-chapter-btn"
              >
                Add Chapter
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Export Modal */}
      <AnimatePresence>
        {showExportModal && (
          <motion.div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="bg-[#121214] border border-white/10 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              data-testid="export-video-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-xl flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  <VideoCamera size={24} className="text-purple-500" />
                  Export Video
                </h2>
                <button onClick={() => setShowExportModal(false)} className="p-1 hover:bg-white/10 rounded">
                  <X size={20} />
                </button>
              </div>

              {/* Video Title */}
              <div className="mb-4">
                <label className="text-xs text-zinc-500 block mb-1">Video Title</label>
                <input
                  type="text"
                  value={exportTitle}
                  onChange={(e) => setExportTitle(e.target.value)}
                  className="w-full bg-transparent border border-white/10 rounded-sm px-3 py-2 focus:outline-none focus:border-purple-500"
                  placeholder="My Story Animation"
                  data-testid="export-title-input"
                />
              </div>

              {/* Chapter Selection */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-zinc-500">Select Chapters to Include</label>
                  <button 
                    onClick={selectAllChaptersForExport}
                    className="text-xs text-purple-400 hover:text-purple-300"
                  >
                    Select All
                  </button>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto border border-white/10 rounded-sm p-2">
                  {chapters.filter(ch => ch.status === "analyzed" && ch.scenes?.length > 0).map(chapter => (
                    <label 
                      key={chapter.id}
                      className="flex items-center gap-2 p-2 hover:bg-white/5 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedChaptersForExport.includes(chapter.id)}
                        onChange={() => toggleChapterForExport(chapter.id)}
                        className="accent-purple-500"
                      />
                      <span className="text-sm">Ch {chapter.chapter_number}: {chapter.title}</span>
                      <span className="text-xs text-zinc-500 ml-auto">{chapter.scenes?.length} scenes</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  {selectedChaptersForExport.length} chapters selected • 
                  {chapters.filter(ch => selectedChaptersForExport.includes(ch.id)).reduce((acc, ch) => acc + (ch.scenes?.length || 0), 0)} total scenes
                </p>
              </div>

              {/* Progress */}
              {isExporting && (
                <div className="mb-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">Exporting...</span>
                    <span className="text-purple-400">{exportProgress.progress}%</span>
                  </div>
                  <div className="w-full bg-zinc-800 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${exportProgress.progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-zinc-500">{exportProgress.message}</p>
                </div>
              )}

              {/* Download Link */}
              {exportProgress.downloadUrl && !isExporting && (
                <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
                  <p className="text-green-400 text-sm mb-2">✅ Video exported successfully!</p>
                  <a
                    href={`${BACKEND_URL}${exportProgress.downloadUrl}`}
                    download
                    className="flex items-center justify-center gap-2 w-full py-2 bg-green-500 text-green-950 font-medium rounded-sm hover:bg-green-400 transition-colors"
                  >
                    <Download size={18} />
                    Download Video
                  </a>
                </div>
              )}

              {/* Export Button */}
              {!exportProgress.downloadUrl && (
                <button
                  onClick={exportVideo}
                  disabled={isExporting || selectedChaptersForExport.length === 0}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-sm hover:from-purple-500 hover:to-pink-500 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="start-export-btn"
                >
                  {isExporting ? (
                    <>
                      <Spinner size={18} className="animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Export size={18} />
                      Export {selectedChaptersForExport.length} Chapters
                    </>
                  )}
                </button>
              )}

              {/* Previously Exported Videos */}
              {exportedVideos.length > 0 && (
                <div className="mt-6 pt-4 border-t border-white/10">
                  <h3 className="text-sm text-zinc-400 mb-2">Previously Exported</h3>
                  <div className="space-y-2">
                    {exportedVideos.map(video => (
                      <div key={video.id} className="flex items-center justify-between p-2 bg-white/5 rounded-sm">
                        <span className="text-sm">{video.filename}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-500">{video.size_mb} MB</span>
                          <a
                            href={`${BACKEND_URL}/api/export/download/${video.id}`}
                            download
                            className="p-1 hover:bg-white/10 rounded"
                          >
                            <Download size={16} />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
